import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { get, del, getWithParams } from '../../utils/fetchAPI';
import { FaEdit, FaTrash, FaPlus, FaTimes } from 'react-icons/fa';
import PageTitle from "../../components/page-title/pageTitle";
import { FaClipboardList } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import Loader from "../../components/loader/loder";
import CustomPagination from "../../components/pagination/CustomPagination";
import { swalDel } from "../../utils/SwalUti";
import { toast } from "react-toastify";
import FollowupTestCreate from "./followup-test-create";

const FollowupTestList = () => {
    const [tests, setTests] = useState([]);
    const [selectedTest, setSelectedTest] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [showDoctorsModal, setShowDoctorsModal] = useState(false);
    const [selectedDoctors, setSelectedDoctors] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [totalRows, setTotalRows] = useState(0);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState('');
    const [doctorFilter, setDoctorFilter] = useState('');
    const [doctors, setDoctors] = useState([]);

    useEffect(() => {
        fetchTests();
    }, [currentPage, perPage, search, activeFilter, doctorFilter]);

    useEffect(() => {
        // Fetch doctors for filter dropdown
        const fetchDoctors = async () => {
            try {
                const response = await getWithParams('/doctor/list-v2', {
                    per_page: 100,
                    current_page: 1,
                });
                if (response?.status === 200) {
                    setDoctors(response.data?.doctors || []);
                }
            } catch (error) {
                console.error('Error fetching doctors:', error);
            }
        };
        fetchDoctors();
    }, []);

    const fetchTests = async () => {
        setIsLoading(true);
        try {
            const params = {
                per_page: perPage,
                current_page: currentPage,
            };

            if (search) {
                params.search = search;
            }

            if (activeFilter) {
                params.active = activeFilter === 'true';
            }

            if (doctorFilter) {
                params.doctor = doctorFilter;
            }

            const response = await getWithParams('/followup-test/list', params);

            if (response?.status === 200) {
                setTests(response.data?.followupTests || []);
                setTotalRows(response.data?.total || 0);
            }
        } catch (error) {
            console.error('Error fetching follow-up tests:', error);
            toast.error('Failed to fetch follow-up tests', {
                position: "top-center",
                theme: "light",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (test) => {
        setSelectedTest(test);
        setShowPopup(true);
    };

    const handleCreateTest = (newTest) => {
        if (selectedTest) {
            // Update test in the list
            setTests(tests.map(test => test._id === newTest._id ? newTest : test));
        } else {
            // Add new test to the list
            setTests([newTest, ...tests]);
        }
        setShowPopup(false);
        fetchTests(); // Refresh the list
    };

    const handleDelete = async (test) => {
        const result = await swalDel(`Are you sure you want to delete "${test.name}"? This action cannot be undone.`);

        if (result.isConfirmed) {
            try {
                await del(`/followup-test/${test._id}`);
                toast.success('Follow-up test deleted successfully!', {
                    position: "top-center",
                    theme: "light",
                });
                fetchTests();
            } catch (error) {
                console.error('Error deleting follow-up test:', error);
                toast.error('Failed to delete follow-up test. Please try again.', {
                    position: "top-center",
                    theme: "light",
                });
            }
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);
        setCurrentPage(1); // Reset to first page on search
    };

    const handleActiveFilterChange = (e) => {
        setActiveFilter(e.target.value);
        setCurrentPage(1); // Reset to first page on filter change
    };

    const handleDoctorFilterChange = (e) => {
        setDoctorFilter(e.target.value);
        setCurrentPage(1); // Reset to first page on filter change
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row?.name,
            sortable: true,
            wrap: true,
        },
        {
            name: 'Doctor',
            selector: row => {
                const doctors = row?.doctor;
                if (!doctors || doctors.length === 0) return 'All Doctors';
                if (Array.isArray(doctors)) {
                    return doctors.map(d => typeof d === 'string' ? d : d.name).join(', ');
                }
                return typeof doctors === 'string' ? 'Specific Doctor' : doctors.name || 'Specific Doctor';
            },
            sortable: true,
            wrap: true,
            cell: (row) => {
                const doctors = row?.doctor;

                const handleViewDoctors = () => {
                    setSelectedDoctors(doctors);
                    setShowDoctorsModal(true);
                };

                // No doctors selected = available for all
                if (!doctors || doctors.length === 0) {
                    return (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                            All Doctors
                        </span>
                    );
                }

                // Handle array of doctors
                if (Array.isArray(doctors)) {
                    // Show individual badges for 1-2 doctors with click to view all
                    if (doctors.length <= 2) {
                        return (
                            <div
                                className="flex flex-wrap gap-1 cursor-pointer hover:opacity-80"
                                onClick={handleViewDoctors}
                                title="Click to view all doctors"
                            >
                                {doctors.map((doctor, idx) => (
                                    <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                        {typeof doctor === 'string' ? doctor : doctor.name}
                                    </span>
                                ))}
                            </div>
                        );
                    }

                    // Show condensed format for 3+ doctors with clickable badge
                    const firstTwo = doctors.slice(0, 2);
                    const remaining = doctors.length - 2;
                    return (
                        <div
                            className="flex flex-wrap gap-1 items-center cursor-pointer hover:opacity-80"
                            onClick={handleViewDoctors}
                            title="Click to view all doctors"
                        >
                            {firstTwo.map((doctor, idx) => (
                                <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                    {typeof doctor === 'string' ? doctor : doctor.name}
                                </span>
                            ))}
                            <span className="px-2 py-1 bg-blue-200 text-blue-900 rounded text-xs font-medium hover:bg-blue-300">
                                +{remaining} more
                            </span>
                        </div>
                    );
                }

                // Handle single doctor (legacy support)
                return (
                    <span
                        className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm cursor-pointer hover:opacity-80"
                        onClick={handleViewDoctors}
                        title="Click to view doctor details"
                    >
                        {typeof doctors === 'string' ? 'Specific Doctor' : doctors.name || 'Specific Doctor'}
                    </span>
                );
            },
        },
        {
            name: 'Status',
            selector: row => row?.active ? 'Active' : 'Inactive',
            sortable: true,
            cell: (row) => (
                <span className={`px-2 py-1 rounded text-sm ${row?.active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-gray-800'
                    }`}>
                    {row?.active ? 'Active' : 'Inactive'}
                </span>
            ),
        },
        {
            name: 'Breakdowns',
            selector: row => row?.breakdowns?.length || 0,
            sortable: true,
            cell: (row) => (
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm">
                    {row?.breakdowns?.length || 0} {row?.breakdowns?.length === 1 ? 'item' : 'items'}
                </span>
            ),
        },
        {
            name: 'Order',
            selector: row => row?.order ?? 0,
            sortable: true,
            width: '120px',
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="flex gap-2">
                    <button
                        className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={() => handleEdit(row)}
                        title="Edit"
                    >
                        <FaEdit />
                    </button>
                    {/* <button
                        className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={() => handleDelete(row)}
                        title="Delete"
                    >
                        <FaTrash />
                    </button> */}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#50b6b5',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 'bold',
            },
        },
        rows: {
            style: {
                fontSize: '0.9rem',
            },
            stripedStyle: {
                backgroundColor: '#f6f6f6',
            },
            highlightOnHoverStyle: {
                backgroundColor: '#e0e0e0',
            },
        },
    };

    return (
        <div className="flex justify-center flex-col gap-5 w-full">
            <div className="flex justify-between items-center">
                <PageTitle title="Follow Up Sheet Data" icon={<FaClipboardList />} />
                <button
                    onClick={() => {
                        setSelectedTest(null);
                        setShowPopup(true);
                    }}
                    className="flex items-center justify-center gap-2 text-sm px-4 py-2 bg-primary text-white rounded-md hover:bg-teal-700"
                >
                    <FaPlus className="w-4 h-4" />
                    Create New Test
                </button>
            </div>

            {/* Search and Filter Section */}
            <div className="bg-white rounded-md p-4 shadow-md">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <input
                            type="text"
                            value={search}
                            onChange={handleSearch}
                            placeholder="Search by test name..."
                            className="w-full text-sm px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-teal-800"
                        />
                    </div>
                    <div className="w-full md:w-48">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Active Status</label>
                        <select
                            value={activeFilter}
                            onChange={handleActiveFilterChange}
                            className="w-full text-sm px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-teal-800"
                        >
                            <option value="">All</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                    <div className="w-full md:w-56">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Doctor</label>
                        <select
                            value={doctorFilter}
                            onChange={handleDoctorFilterChange}
                            className="w-full text-sm px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-teal-800"
                        >
                            <option value="">All Tests</option>
                            <option value="null">Available for All Doctors</option>
                            {doctors.map(doctor => (
                                <option key={doctor._id} value={doctor._id}>
                                    {doctor.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="text-center text-gray-500"><Loader /></div>
            ) : (
                <DataTable
                    columns={columns}
                    data={tests}
                    customStyles={customStyles}
                    highlightOnHover
                    striped
                    noDataComponent="No follow-up tests found"
                />
            )}

            <CustomPagination
                currentPage={currentPage}
                totalRows={totalRows}
                perPage={perPage}
                setCurrentPage={setCurrentPage}
                setPerPage={setPerPage}
            />

            <AnimatePresence>
                {showPopup && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            className="bg-white p-5 rounded-md shadow-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                        >
                            <FollowupTestCreate
                                test={selectedTest}
                                createTest={handleCreateTest}
                                fetchTests={fetchTests}
                                onClose={() => {
                                    setShowPopup(false);
                                    setSelectedTest(null);
                                }}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Doctors Modal */}
            <AnimatePresence>
                {showDoctorsModal && (
                    <div
                        className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50"
                        onClick={() => setShowDoctorsModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            className="bg-white p-6 rounded-md shadow-lg max-w-md w-full mx-4"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-gray-800">Tagged Doctors</h3>
                                <button
                                    onClick={() => setShowDoctorsModal(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <FaTimes className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="max-h-96 overflow-y-auto">
                                {selectedDoctors && selectedDoctors.length > 0 ? (
                                    <ul className="space-y-2">
                                        {selectedDoctors.map((doctor, idx) => (
                                            <li
                                                key={idx}
                                                className="px-3 py-2 bg-blue-50 text-blue-800 rounded border border-blue-200"
                                            >
                                                {typeof doctor === 'string' ? doctor : doctor.name}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-gray-500 text-center py-4">No doctors tagged</p>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default FollowupTestList;
